import type { User } from "firebase/auth";
import { type FormEvent, useEffect, useRef, useState } from "react";
import * as api from "./api";
import { alertProblem, chatText } from "./telegramAlerts";
import { SkeletonSection } from "./Skeleton";

/** Rules AL-5 to AL-8: the sensor rule issues that go to a Telegram channel of the cell. */
export function TelegramAlertSettings({ user }: { user: User }) {
  const [found, setFound] = useState<api.TelegramAlerts | "failed" | undefined>(undefined);
  const [enabled, setEnabled] = useState(false);
  const [chat, setChat] = useState("");
  const [rules, setRules] = useState<string[]>([]);
  const [problem, setProblem] = useState<string | null>(null);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const status = useRef<HTMLParagraphElement>(null);

  const show = (next: api.TelegramAlerts) => {
    setFound(next);
    setEnabled(next.enabled);
    setChat(next.chat_id || next.chats[0] || "");
    setRules(next.rules);
  };

  useEffect(() => {
    user
      .getIdToken()
      .then(api.telegramAlerts)
      .then(show, () => setFound("failed"));
  }, [user]);

  if (found === undefined) return <SkeletonSection title="Telegram alerts" rows={8} />;
  if (found === "failed") {
    return (
      <fieldset>
        <legend>Telegram alerts</legend>
        <p className="error">Cannot load the Telegram alerts. Try again later.</p>
      </fieldset>
    );
  }
  const edit = () => {
    setMessage(null);
    setProblem(null);
  };
  const save = async (event: FormEvent) => {
    event.preventDefault();
    const wrong = alertProblem(enabled, chat, rules);
    if (wrong) {
      setMessage(null);
      setProblem(wrong);
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      const next = await api.setTelegramAlerts(await user.getIdToken(), { enabled, chat_id: chat, rules });
      show(next);
      setMessage({ ok: true, text: next.enabled ? "Saved. The alerts are on." : "Saved. The alerts are off." });
      requestAnimationFrame(() => status.current?.focus());
    } catch (e) {
      const detail = e instanceof api.ApiError && e.status < 500 ? e.detail : undefined;
      setMessage({ ok: false, text: detail ?? "The Telegram alerts were not saved. Try again." });
    } finally {
      setSaving(false);
    }
  };
  const noChat = found.chats.length === 0;
  return (
    <form onSubmit={save} noValidate data-testid="telegram-alerts">
      <fieldset disabled={!found.can_edit || saving}>
        <legend>Telegram alerts</legend>
        <p className="hint">
          Each 15 minutes, the new sensor issues of the kinds below go to the Telegram channel in one message, and a short note goes when an issue is closed. The message gives the kind, the account name and number, the container, and a link to the Issues page. It never gives the street address.
        </p>
        {noChat && (
          <p className="hint" data-testid="telegram-no-chat">
            This cell has no Telegram channel yet. An operator adds it.
          </p>
        )}
        <div className="limit">
          <label>
            <input
              type="checkbox"
              data-testid="telegram-enabled"
              checked={enabled}
              disabled={noChat && !enabled}
              onChange={(e) => {
                setEnabled(e.target.checked);
                edit();
              }}
            />
            Send the alerts of this organization
          </label>
        </div>
        <div className="limit">
          <label htmlFor="telegram-chat">Channel</label>
          <select
            id="telegram-chat"
            data-testid="telegram-chat"
            value={chat}
            disabled={noChat}
            onChange={(e) => {
              setChat(e.target.value);
              edit();
            }}
          >
            {found.chats.map((id, index) => (
              <option key={id} value={id}>
                {chatText(id, index)}
              </option>
            ))}
          </select>
        </div>
        <div className="table-scroll" tabIndex={0} role="region" aria-label="Issue kinds to send">
          <table className="list" data-testid="telegram-rules">
            <thead>
              <tr>
                <th scope="col">Issue kind</th>
                <th scope="col">Send</th>
              </tr>
            </thead>
            <tbody>
              {found.choices.map((choice) => (
                <tr key={choice.rule} data-testid="telegram-rule" data-rule={choice.rule}>
                  <th scope="row">{choice.label}</th>
                  <td data-label="Send">
                    <input
                      type="checkbox"
                      aria-label={`Send ${choice.label}`}
                      data-testid={`telegram-rule-${choice.rule}`}
                      checked={rules.includes(choice.rule)}
                      onChange={(e) => {
                        setRules(e.target.checked ? [...rules, choice.rule] : rules.filter((rule) => rule !== choice.rule));
                        edit();
                      }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {problem && (
          <p role="alert" className="error" data-testid="telegram-error">
            {problem}
          </p>
        )}
        {found.can_edit && (
          <div className="actions">
            <button className="primary" data-testid="save-telegram-alerts">
              {saving ? "Saving…" : "Save the alerts"}
            </button>
          </div>
        )}
      </fieldset>
      <p ref={status} tabIndex={-1} role="status" data-testid="telegram-status" className={message && !message.ok ? "error" : undefined}>
        {message?.text}
      </p>
    </form>
  );
}
